import React, { useState } from "react";
import axiosInstance from "../axiosInstance";
import { useNavigate } from "react-router-dom";

const STEPS = { EMAIL: "email", CODE: "code", PASSWORD: "password", DONE: "done" };

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(STEPS.EMAIL);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axiosInstance.post("/api/forgotPassword", { email });
      setStep(STEPS.CODE);
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axiosInstance.post("/api/verifyResetCode", { email, code });
      setResetToken(res.data.resetToken);
      setStep(STEPS.PASSWORD);
    } catch (err) {
      setError(err.response?.data?.message || "Invalid or expired code.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setLoading(true);
    try {
      await axiosInstance.post("/api/resetPasswordWithCode", { resetToken, newPassword });
      setStep(STEPS.DONE);
    } catch (err) {
      setError(err.response?.data?.message || "Error resetting password. Please start again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">

        {/* Progress dots */}
        <div className="flex justify-center gap-3 mb-8">
          {[STEPS.EMAIL, STEPS.CODE, STEPS.PASSWORD].map((s, i) => (
            <div
              key={s}
              className={`w-3 h-3 rounded-full transition-all ${
                step === STEPS.DONE || [STEPS.EMAIL, STEPS.CODE, STEPS.PASSWORD].indexOf(step) >= i
                  ? "bg-indigo-600"
                  : "bg-gray-200"
              }`}
            />
          ))}
        </div>

        {/* ── STEP 1: Enter email ── */}
        {step === STEPS.EMAIL && (
          <>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Forgot Password</h1>
            <p className="text-gray-500 text-sm mb-6">
              Enter your email and we'll send you a 5-digit code.
            </p>
            <form onSubmit={handleSendCode} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@example.com"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              {error && <p className="text-red-500 text-sm">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2.5 rounded-lg transition disabled:opacity-60"
              >
                {loading ? "Sending..." : "Send Code"}
              </button>
            </form>
            <p className="text-center text-sm text-gray-500 mt-5">
              Remember your password?{" "}
              <button onClick={() => navigate("/login")} className="text-indigo-600 hover:underline font-medium">
                Sign in
              </button>
            </p>
          </>
        )}

        {/* ── STEP 2: Enter code ── */}
        {step === STEPS.CODE && (
          <>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Enter Code</h1>
            <p className="text-gray-500 text-sm mb-6">
              We sent a 5-digit code to <strong>{email}</strong>. It expires in 15 minutes.
            </p>
            <form onSubmit={handleVerifyCode} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">5-Digit Code</label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 5))}
                  required
                  placeholder="12345"
                  maxLength={5}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-center text-2xl font-bold tracking-widest focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              {error && <p className="text-red-500 text-sm">{error}</p>}
              <button
                type="submit"
                disabled={loading || code.length !== 5}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2.5 rounded-lg transition disabled:opacity-60"
              >
                {loading ? "Verifying..." : "Verify Code"}
              </button>
            </form>
            <button
              onClick={() => { setStep(STEPS.EMAIL); setError(""); setCode(""); }}
              className="w-full text-center text-sm text-gray-500 hover:text-gray-700 mt-4"
            >
              ← Use a different email
            </button>
          </>
        )}

        {/* ── STEP 3: New password ── */}
        {step === STEPS.PASSWORD && (
          <>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">New Password</h1>
            <p className="text-gray-500 text-sm mb-6">Choose a strong password (min. 8 characters).</p>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              {error && <p className="text-red-500 text-sm">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2.5 rounded-lg transition disabled:opacity-60"
              >
                {loading ? "Saving..." : "Reset Password"}
              </button>
            </form>
          </>
        )}

        {/* ── DONE ── */}
        {step === STEPS.DONE && (
          <div className="text-center">
            <div className="text-green-500 text-6xl mb-4">✓</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Password Reset!</h1>
            <p className="text-gray-500 text-sm mb-6">Your password has been changed successfully.</p>
            <button
              onClick={() => navigate("/login")}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2.5 rounded-lg transition"
            >
              Sign In
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
